import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AgentHeader } from "@/components/AgentHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader, StageIcon, money } from "@/components/shared";
import { AgentTours } from "@/components/AgentTours";
import { PlacesManager } from "@/components/PlacesManager";
import { DocumentsManager } from "@/components/DocumentsManager";
import { MlsUpload } from "@/components/MlsUpload";
import { ClientProfileEditor } from "@/components/ClientProfileEditor";
import { ClosingManager } from "@/components/ClosingManager";
import { JOURNEY_STAGES, stageIndex, currentStageKey } from "@/lib/journey";
import { ArrowLeft, Copy, Loader2, Send, Plus, UserPlus, Trash2, Eye } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reply, setReply] = useState({});
  const [guide, setGuide] = useState({ title: "", body: "" });
  const [res, setRes] = useState({ title: "", url: "", category: "Lender", description: "" });
  const [cobuyer, setCobuyer] = useState({ name: "", email: "", phone: "" });
  const [cbInvite, setCbInvite] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["client", id], queryFn: async () => (await api.get(`/agent/clients/${id}`)).data,
  });

  const update = useMutation({
    mutationFn: async (patch) => (await api.patch(`/agent/clients/${id}`, patch)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const replyMut = useMutation({
    mutationFn: async ({ qid, text }) => (await api.post(`/agent/questions/${qid}/reply`, { reply: text })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); setReply({}); toast.success("Reply sent"); },
  });
  const guideMut = useMutation({
    mutationFn: async () => (await api.post("/agent/guidance", { client_id: id, type: "guidance", ...guide })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); setGuide({ title: "", body: "" }); toast.success("Guidance added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const resMut = useMutation({
    mutationFn: async () => (await api.post("/agent/resources", { client_id: id, ...res })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); setRes({ title: "", url: "", category: "Lender", description: "" }); toast.success("Resource added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const addCobuyer = useMutation({
    mutationFn: async () => (await api.post(`/agent/clients/${id}/cobuyers`, cobuyer)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["client", id] }); setCobuyer({ name: "", email: "", phone: "" }); setCbInvite(d); toast.success("Co-buyer added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const removeCobuyer = useMutation({
    mutationFn: async (email) => (await api.delete(`/agent/clients/${id}/cobuyers/${encodeURIComponent(email)}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Removed"); },
  });
  const previewLink = useMutation({
    mutationFn: async (rotate = false) => (await api.post(`/agent/clients/${id}/preview-link${rotate ? "?rotate=true" : ""}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Share link ready"); },
  });
  const revokePreview = useMutation({
    mutationFn: async () => (await api.delete(`/agent/clients/${id}/preview-link`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Link disabled"); },
  });

  if (isLoading) return (<div className="min-h-screen bg-background"><AgentHeader /><PageLoader /></div>);

  const { client, homes, questions, guidance, resources, roadmap, invite_link, preview_link, co_buyers = [], tours = [] } = data;
  const profile = client.profile || {};
  const pct = Math.round((roadmap.stages.filter((s) => s.status === "complete").length / roadmap.stages.length) * 100);

  return (
    <div className="min-h-screen bg-background">
      <AgentHeader />
      <main className="max-w-6xl mx-auto p-4 sm:p-8">
        <Button variant="ghost" onClick={() => navigate("/agent")} className="mb-4 gap-2" data-testid="back-to-clients"><ArrowLeft className="h-4 w-4" /> All clients</Button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold">{client.name}</h1>
            <p className="text-muted-foreground">{client.email}{client.phone ? ` · ${client.phone}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="capitalize bg-secondary text-foreground border-0 text-sm px-3 py-1">{client.status}</Badge>
            <Button variant="outline" className="rounded-full gap-2" onClick={() => navigate(`/agent/clients/${id}/preview`)} data-testid="preview-as-client-btn">
              <Eye className="h-4 w-4" /> Preview as client
            </Button>
          </div>
        </div>

        <Card className="p-5 rounded-2xl mb-6">
          <div className="flex justify-between text-sm mb-2"><span className="font-medium">Roadmap progress</span><span>{pct}%</span></div>
          <Progress value={pct} className="h-2 mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {roadmap.stages.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-sm"><StageIcon status={s.status} className="h-4 w-4" /><span className="truncate">{s.title}</span></div>
            ))}
          </div>
        </Card>

        <Tabs defaultValue="overview">
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Profile</TabsTrigger>
            <TabsTrigger value="homes" data-testid="tab-homes">Homes ({homes.length})</TabsTrigger>
            <TabsTrigger value="tours" data-testid="tab-tours">Tours ({tours.length})</TabsTrigger>
            <TabsTrigger value="mls" data-testid="tab-mls">MLS Upload</TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions">Questions ({questions.length})</TabsTrigger>
            <TabsTrigger value="guidance" data-testid="tab-guidance">Guidance</TabsTrigger>
            <TabsTrigger value="household" data-testid="tab-household">Household ({co_buyers.length + 1})</TabsTrigger>
            <TabsTrigger value="closing" data-testid="tab-closing">Contract &amp; Anniversary</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">Access</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <ClientProfileEditor clientId={id} profile={profile} />
              </div>
              <Card className="p-5 rounded-2xl h-fit">
                <h3 className="font-heading font-semibold mb-1">Home-buying journey</h3>
                <p className="text-xs text-muted-foreground mb-3">Tap a stage to set where {client.name?.split(" ")[0]} is. The buyer sees this but can't change it.</p>
                <div className="space-y-1.5">
                  {JOURNEY_STAGES.map((s, idx) => {
                    const curIdx = stageIndex(currentStageKey(client));
                    const isCur = idx === curIdx; const isDone = idx < curIdx;
                    return (
                      <button key={s.key} onClick={() => update.mutate({ journey_stage: s.key })} data-testid={`set-stage-${s.key}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${isCur ? "bg-primary text-primary-foreground" : isDone ? "bg-status-complete/10 text-foreground hover:bg-status-complete/20" : "hover:bg-secondary text-muted-foreground"}`}>
                        <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isCur ? "bg-primary-foreground text-primary" : isDone ? "bg-status-complete text-white" : "bg-muted"}`}>{isDone ? "✓" : idx + 1}</span>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="homes">
            {homes.length === 0 ? <Card className="p-8 rounded-2xl text-center text-muted-foreground">No homes added yet.</Card> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {homes.map((h) => (
                  <Card key={h.id} className="p-5 rounded-2xl">
                    <div className="flex justify-between"><p className="font-semibold">{h.title}</p><span className="text-primary font-medium">{money(h.price)}</span></div>
                    <p className="text-sm text-muted-foreground">{h.address || "—"}</p>
                    {h.notes && <p className="text-sm mt-2">{h.notes}</p>}
                    {h.link && <a href={h.link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">View listing</a>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tours">
            <div className="mb-4">
              <PlacesManager
                destinations={client.destinations || []}
                addFn={(d) => api.post(`/agent/clients/${id}/destinations`, d)}
                removeFn={(did) => api.delete(`/agent/clients/${id}/destinations/${did}`)}
                onChange={() => qc.invalidateQueries({ queryKey: ["client", id] })}
                heading="Places that matter (commute references)"
                subtitle={`Add ${client.name?.split(" ")[0]}'s key places (work, school, etc.). They appear as one-tap directions on every tour home. Drive times are shown by Maps — never estimated by us.`}
              />
            </div>
            <AgentTours clientId={id} tours={tours} criteria={[...(profile.must_haves || []), ...(profile.nice_to_haves || [])]} />
          </TabsContent>

          <TabsContent value="closing">
            <ClosingManager clientId={id} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsManager clientId={id} />
          </TabsContent>

          <TabsContent value="mls">
            <MlsUpload clientId={id} />
          </TabsContent>

          <TabsContent value="questions">
            {questions.length === 0 ? <Card className="p-8 rounded-2xl text-center text-muted-foreground">No questions yet.</Card> : (
              <div className="space-y-3">
                {questions.map((q) => (
                  <Card key={q.id} className="p-5 rounded-2xl" data-testid={`agent-question-${q.id}`}>
                    <div className="flex justify-between gap-2"><p className="font-semibold">{q.question}</p>
                      <Badge className={`border-0 shrink-0 ${q.status === "escalated" ? "bg-status-warning/15 text-status-warning" : q.status === "resolved" ? "bg-status-complete/15 text-status-complete" : "bg-secondary text-muted-foreground"}`}>{q.status}</Badge>
                    </div>
                    {q.answer && <p className="text-sm text-muted-foreground mt-2 pl-3 border-l-2 border-border">{q.answer}</p>}
                    {q.agent_reply ? (
                      <p className="text-sm mt-2 p-3 bg-forest/5 rounded-xl border border-forest/20">{q.agent_reply}</p>
                    ) : (
                      <div className="flex gap-2 mt-3">
                        <Input value={reply[q.id] || ""} onChange={(e) => setReply({ ...reply, [q.id]: e.target.value })} placeholder="Write a reply…" data-testid={`reply-input-${q.id}`} />
                        <Button onClick={() => replyMut.mutate({ qid: q.id, text: reply[q.id] })} disabled={!reply[q.id] || replyMut.isPending} data-testid={`reply-send-${q.id}`} className="shrink-0"><Send className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="guidance">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5 rounded-2xl">
                <h3 className="font-heading font-semibold mb-3">Add guidance note</h3>
                <div className="space-y-3">
                  <Input value={guide.title} onChange={(e) => setGuide({ ...guide, title: e.target.value })} placeholder="Title" data-testid="guidance-title-input" />
                  <Textarea value={guide.body} onChange={(e) => setGuide({ ...guide, body: e.target.value })} placeholder="Message for your client…" data-testid="guidance-body-input" className="rounded-xl" />
                  <Button onClick={() => guideMut.mutate()} disabled={!guide.title || !guide.body || guideMut.isPending} data-testid="guidance-add-btn" className="rounded-full gap-1"><Plus className="h-4 w-4" /> Add guidance</Button>
                </div>
                <div className="mt-4 space-y-2">
                  {guidance.map((g) => <div key={g.id} className="text-sm p-3 bg-secondary rounded-xl"><p className="font-medium">{g.title}</p><p className="text-muted-foreground">{g.body}</p></div>)}
                </div>
              </Card>
              <Card className="p-5 rounded-2xl">
                <h3 className="font-heading font-semibold mb-3">Add a custom resource link</h3>
                <div className="space-y-3">
                  <Input value={res.title} onChange={(e) => setRes({ ...res, title: e.target.value })} placeholder="Title" data-testid="resource-title-input" />
                  <Input value={res.url} onChange={(e) => setRes({ ...res, url: e.target.value })} placeholder="https://…" data-testid="resource-url-input" />
                  <Select value={res.category} onValueChange={(v) => setRes({ ...res, category: v })}>
                    <SelectTrigger data-testid="resource-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Lender", "Inspection", "Schools", "Public Safety", "Flood / Hazard", "Tax / Public Records", "Utilities", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={res.description} onChange={(e) => setRes({ ...res, description: e.target.value })} placeholder="Short description" />
                  <Button onClick={() => resMut.mutate()} disabled={!res.title || !res.url || resMut.isPending} data-testid="resource-add-btn" className="rounded-full gap-1"><Plus className="h-4 w-4" /> Add resource</Button>
                </div>
                <div className="mt-4 space-y-2">
                  {resources.map((r) => <div key={r.id} className="text-sm p-3 bg-secondary rounded-xl"><p className="font-medium">{r.title}</p><p className="text-muted-foreground text-xs">{r.category}</p></div>)}
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="household">
            <Card className="p-5 rounded-2xl max-w-xl space-y-5">
              <div>
                <h3 className="font-heading font-semibold mb-1">Shared household</h3>
                <p className="text-sm text-muted-foreground">Add a co-buyer (partner or spouse). Everyone in the household shares the same homes, notes and comparisons.</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary">
                  <div className="flex-1 min-w-0"><p className="font-medium text-sm">{client.name}</p><p className="text-xs text-muted-foreground truncate">{client.email}</p></div>
                  <Badge className="bg-forest/15 text-forest border-0">Primary</Badge>
                </div>
                {co_buyers.map((cb) => (
                  <div key={cb.email} className="flex items-center gap-2 p-3 rounded-xl bg-secondary" data-testid={`cobuyer-row-${cb.email}`}>
                    <div className="flex-1 min-w-0"><p className="font-medium text-sm">{cb.name}</p><p className="text-xs text-muted-foreground truncate">{cb.email}</p></div>
                    <Badge className={`border-0 ${cb.buyer_user_id ? "bg-status-complete/15 text-status-complete" : "bg-status-warning/15 text-status-warning"}`}>{cb.buyer_user_id ? "Joined" : "Invited"}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => navigator.clipboard.writeText(cb.invite_link).then(() => toast.success("Invite link copied"))} data-testid={`cobuyer-copy-${cb.email}`}><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeCobuyer.mutate(cb.email)} data-testid={`cobuyer-remove-${cb.email}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="font-medium text-sm">Add a co-buyer</h4>
                <Input value={cobuyer.name} onChange={(e) => setCobuyer({ ...cobuyer, name: e.target.value })} placeholder="Name" data-testid="cobuyer-name-input" />
                <Input type="email" value={cobuyer.email} onChange={(e) => setCobuyer({ ...cobuyer, email: e.target.value })} placeholder="Email" data-testid="cobuyer-email-input" />
                <Button onClick={() => addCobuyer.mutate()} disabled={!cobuyer.name || !cobuyer.email || addCobuyer.isPending} data-testid="cobuyer-add-btn" className="rounded-full gap-1"><UserPlus className="h-4 w-4" /> Add co-buyer</Button>
                {cbInvite && (
                  <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 text-sm">
                    <p className="mb-2">Share this invite link with <strong>{cbInvite.name}</strong>:</p>
                    <div className="flex gap-2"><Input readOnly value={cbInvite.invite_link} className="text-xs" data-testid="cobuyer-invite-link" /><Button variant="outline" onClick={() => navigator.clipboard.writeText(cbInvite.invite_link).then(() => toast.success("Copied"))}><Copy className="h-4 w-4" /></Button></div>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="access">
            <Card className="p-5 rounded-2xl max-w-xl space-y-5">
              <div>
                <Label className="mb-2 block">Invite link</Label>
                <div className="flex gap-2">
                  <Input readOnly value={invite_link} data-testid="detail-invite-link" className="text-xs" />
                  <Button variant="outline" onClick={() => { navigator.clipboard.writeText(invite_link); toast.success("Copied"); }} data-testid="detail-copy-invite"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Shareable read-only preview link</Label>
                <p className="text-xs text-muted-foreground mb-2">Anyone with this link can view {client.name?.split(" ")[0]}'s workspace read-only — no login needed. Agent-only notes stay hidden.</p>
                {preview_link ? (
                  <>
                    <div className="flex gap-2">
                      <Input readOnly value={preview_link} data-testid="preview-share-link" className="text-xs" />
                      <Button variant="outline" onClick={() => { navigator.clipboard.writeText(preview_link); toast.success("Copied"); }} data-testid="copy-preview-link"><Copy className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => previewLink.mutate(true)} disabled={previewLink.isPending} data-testid="rotate-preview-link">Regenerate</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => revokePreview.mutate()} disabled={revokePreview.isPending} data-testid="revoke-preview-link">Disable link</Button>
                    </div>
                  </>
                ) : (
                  <Button variant="outline" className="rounded-full gap-2" onClick={() => previewLink.mutate(false)} disabled={previewLink.isPending} data-testid="generate-preview-link"><Eye className="h-4 w-4" /> Generate share link</Button>
                )}
              </div>
              <div>
                <Label className="mb-2 block">Client access status</Label>
                <Select value={client.status} onValueChange={(v) => update.mutate({ status: v })}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["invited", "active", "inactive", "archived"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Pre-approval status</Label>
                <Select value={client.pre_approval_status} onValueChange={(v) => update.mutate({ pre_approval_status: v })}>
                  <SelectTrigger data-testid="preapproval-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["unknown", "in progress", "pre-approved", "not approved"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Stalled after (no activity)</Label>
                <Select value={client.stall_days ? String(client.stall_days) : ""} onValueChange={(v) => update.mutate({ stall_days: Number(v) })}>
                  <SelectTrigger data-testid="stall-days-select"><SelectValue placeholder="Default (7 days)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days (default)</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Active until (optional)</Label>
                <Input type="date" defaultValue={client.active_until || ""} onBlur={(e) => update.mutate({ active_until: e.target.value })} data-testid="active-until-input" />
              </div>
              <div>
                <Label className="mb-2 block">Private agent notes</Label>
                <Textarea defaultValue={client.agent_notes || ""} onBlur={(e) => update.mutate({ agent_notes: e.target.value })} placeholder="Only you can see these…" data-testid="agent-notes-input" className="rounded-xl" />
              </div>
              <Button variant="outline" onClick={() => update.mutate({ status: "archived" })} data-testid="archive-client-btn" className="text-destructive border-destructive/30 rounded-full">
                Archive client
              </Button>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
